import { DatasetSource, DecklineKind, KPIType } from '@services/api/deckManagement/graphql/schema/API';
import { PageSidePanelDropDown } from 'types/PageSidePanel.types';

/**
 * Mapping of KPI Type values to their display labels.
 *
 * @see {@link KPIType}
 * @see {@link PageSidePanelDropDown}
 */
export const KPI_TYPE_LABELS: Record<KPIType, PageSidePanelDropDown> = {
  [KPIType.BASE]: PageSidePanelDropDown.BASE_KPI, // Only BASE is supported for now
  [KPIType.CALCULATED]: PageSidePanelDropDown.CALCULATED_KPI, // Unused currently
  [KPIType.EXTERNAL]: PageSidePanelDropDown.BASE_KPI, // Unused currently. External renders as Base KPI for now
};

/**
 * Read-path map from the wire `DecklineKind` enum (the BE's row-kind vocabulary) to the FE/UI
 * `PageSidePanelDropDown` labels. Used by {@link determineDecklineType} to honour a served
 * `deckline_kind` before falling back to attribute sniffing.
 *
 * `DecklineKind` has no `EXTERNAL` member (the BE accepts it only as a read-only input alias) and no
 * `N/A` sentinel, so every member maps cleanly to a single row-kind label.
 *
 * @see {@link DecklineKind}
 * @see {@link PageSidePanelDropDown}
 */
export const DECKLINE_KIND_LABELS: Record<DecklineKind, PageSidePanelDropDown> = {
  [DecklineKind.BASE]: PageSidePanelDropDown.BASE_KPI,
  [DecklineKind.CALCULATED]: PageSidePanelDropDown.CALCULATED_KPI,
  [DecklineKind.SUBTOTAL]: PageSidePanelDropDown.SUBTOTAL,
  [DecklineKind.BLANKLINE]: PageSidePanelDropDown.BLANK_LINE,
  [DecklineKind.ADJUSTMENT]: PageSidePanelDropDown.ADJUSTMENT,
  [DecklineKind.CALCULATED_ADJUSTMENT]: PageSidePanelDropDown.CALCULATED_ADJUSTMENT,
};

/**
 * Write/inverse of {@link DECKLINE_KIND_LABELS}: FE row-kind label → wire `DecklineKind`. `NONE` (the
 * `N/A` denominator sentinel) is intentionally omitted — it is never a row kind. This is the single
 * place that maps a UI label to a kind member, and it must never produce `EXTERNAL` (not an enum
 * member; the BE accepts it only as a read alias).
 *
 * Used to keep a deckline's `deckline_kind` in sync when its type changes in-session (so the read
 * preference in `determineDecklineType` doesn't resolve a stale served value), and to stamp
 * `deckline_kind` on every saved row at save-prep in `DecklinesContext`.
 *
 * @see {@link DECKLINE_KIND_LABELS}
 */
export const DECKLINE_KIND_BY_LABEL: Partial<Record<PageSidePanelDropDown, DecklineKind>> = {
  [PageSidePanelDropDown.BASE_KPI]: DecklineKind.BASE,
  [PageSidePanelDropDown.CALCULATED_KPI]: DecklineKind.CALCULATED,
  [PageSidePanelDropDown.SUBTOTAL]: DecklineKind.SUBTOTAL,
  [PageSidePanelDropDown.BLANK_LINE]: DecklineKind.BLANKLINE,
  [PageSidePanelDropDown.ADJUSTMENT]: DecklineKind.ADJUSTMENT,
  [PageSidePanelDropDown.CALCULATED_ADJUSTMENT]: DecklineKind.CALCULATED_ADJUSTMENT,
};

/**
 * Mapping of Dataset Source values to their display labels.
 *
 * @see {@link DatasetSource}
 */
export const DATASET_SOURCE_LABELS: Record<DatasetSource, string> = {
  [DatasetSource.GALAXY]: 'Galaxy',
  [DatasetSource.HCTR]: 'HCTR',
};
